import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  useYoutubeOauthStatus,
  useYoutubeOauthDisconnect,
} from "../../lib/youtube";

/**
 * Top-right pill in the YouTube section header. Shows the current OAuth
 * connection state at a glance: green dot + channel title when connected,
 * amber dot + "Not connected" otherwise. Click reveals a dropdown with
 * "Open on YouTube" and "Disconnect" (admin only — already gated by the
 * route entry, so we don't double-check here).
 */
export function ChannelStatusPill() {
  const { data, isLoading } = useYoutubeOauthStatus();
  const disconnect = useYoutubeOauthDisconnect();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("click", onClick);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("click", onClick);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const connected = !!data?.connected;
  const label = isLoading
    ? "Checking…"
    : connected
      ? data?.channelTitle ?? "Connected"
      : "Not connected";

  return (
    <div className="yt-channel-pill-wrap" ref={ref}>
      <button
        type="button"
        className="yt-channel-pill"
        data-state={connected ? "on" : "off"}
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-haspopup="menu"
      >
        <span className="yt-channel-dot" aria-hidden />
        <span className="yt-channel-label font-display-italic">{label}</span>
        <span className="yt-channel-caret" aria-hidden>
          ▾
        </span>
      </button>
      {open && (
        <div className="yt-channel-menu" role="menu">
          {connected ? (
            <>
              <a
                href="https://studio.youtube.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="yt-channel-menu-item"
                role="menuitem"
              >
                Open YouTube Studio ↗
              </a>
              <Link
                to="/dashboard/youtube/advanced"
                className="yt-channel-menu-item"
                role="menuitem"
                onClick={() => setOpen(false)}
              >
                Connection settings
              </Link>
              <button
                type="button"
                className="yt-channel-menu-item is-danger"
                role="menuitem"
                onClick={() => {
                  if (
                    window.confirm(
                      "Disconnect the platform from this channel? This revokes the platform's access at Google. You'll need to reconnect to upload again.",
                    )
                  ) {
                    disconnect.mutate(undefined, {
                      onSuccess: () => setOpen(false),
                    });
                  }
                }}
                disabled={disconnect.isPending}
              >
                {disconnect.isPending ? "Disconnecting…" : "Disconnect"}
              </button>
            </>
          ) : (
            <Link
              to="/dashboard/youtube/advanced"
              className="yt-channel-menu-item"
              role="menuitem"
              onClick={() => setOpen(false)}
            >
              Connect channel →
            </Link>
          )}
        </div>
      )}
    </div>
  );
}
